import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.designinfra.co.in/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isLoginRequest = (config) => Boolean(config?.url && config.url.includes('/login'));

/**
 * Clears the stored session and bounces to the login screen.
 * Guarded so a failure raised while already on /login can't loop the redirect.
 */
const endSession = (message) => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  if (message) toast.error(message);
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // The backend's IsAdmin middleware rejects with HTTP 200 + { status: false },
    // not a 401/403 — so an unauthorized reply arrives here, on the success path.
    // Matched narrowly on the message: no endpoint uses a top-level `status`
    // boolean as its own success flag, but records inside `data` do carry one.
    const body = response?.data;
    if (
      body &&
      typeof body === 'object' &&
      body.status === false &&
      /unauthorized/i.test(body.message ?? '')
    ) {
      endSession(body.message || 'You are not authorized to perform this action.');
      return Promise.reject(new Error(body.message || 'Unauthorized'));
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const config = error.config;

    // Bad credentials come back as a 500, not a 401. Let the login screen show
    // the server's own message instead of a generic "server error" toast.
    if (isLoginRequest(config)) {
      return Promise.reject(error);
    }

    if (status === 401) {
      endSession('Session expired. Please login again.');
    } else if (status === 403) {
      // Left generic on purpose: call sites toast the server's own message
      // (e.g. "System pages cannot be deleted.") via getApiError.
      toast.error('You do not have permission to perform this action.');
    } else if (status === 500) {
      toast.error('Server error. Please try again later.');
    }

    return Promise.reject(error);
  }
);

export default api;
